import type { HistoryPoint, Snapshot } from "@weather-deck/shared";
import { el } from "./dom";

export type DeltaPoint = { ts: number; deck: number; official: number; delta: number };
export type CellStat = { mean: number; n: number };

/**
 * A snapshot's sensor block is re-served for up to 2 h when openSenseMap is
 * unreachable, while the Open-Meteo value keeps updating. Pairing a re-served
 * reading with a fresh forecast value invents a delta, so only pairs where the
 * rooftop reading is close in time to the snapshot are used.
 */
export const MAX_PAIR_AGE_MS = 30 * 60 * 1000;

export function extractDeltas(history: HistoryPoint[], maxAgeMs = MAX_PAIR_AGE_MS): DeltaPoint[] {
  const out: DeltaPoint[] = [];
  for (const p of history) {
    const deck = p.deck;
    const official = p.dwd;
    if (deck == null || official == null || !Number.isFinite(deck) || !Number.isFinite(official)) continue;

    const ts = new Date(p.t).getTime();
    const readingAt = p.deckAt ? new Date(p.deckAt).getTime() : NaN;
    if (!Number.isFinite(ts) || !Number.isFinite(readingAt)) continue;
    if (ts - readingAt > maxAgeMs) continue; // stale rooftop value re-served

    out.push({ ts, deck, official, delta: deck - official });
  }
  return out.sort((a, b) => a.ts - b.ts);
}

export function berlinDayHour(ms: number): { day: string; hour: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hour12: false
  }).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return { day: `${get("year")}-${get("month")}-${get("day")}`, hour: Number(get("hour")) % 24 };
}

function mean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export interface DeltaGrid {
  days: string[];
  rows: Record<string, Array<CellStat | null>>;
}

/** Day × hour grid of mean delta — diurnal pattern reads across, drift reads down. */
export function buildGrid(points: DeltaPoint[], maxDays = 7): DeltaGrid {
  const acc = new Map<string, number[]>();
  for (const p of points) {
    const { day, hour } = berlinDayHour(p.ts);
    const key = `${day}|${hour}`;
    const bucket = acc.get(key);
    if (bucket) bucket.push(p.delta);
    else acc.set(key, [p.delta]);
  }

  const days = [...new Set([...acc.keys()].map((k) => k.split("|")[0]!))].sort().slice(-maxDays);
  const rows: Record<string, Array<CellStat | null>> = {};
  for (const day of days) {
    rows[day] = Array.from({ length: 24 }, (_, h) => {
      const vals = acc.get(`${day}|${h}`);
      return vals ? { mean: mean(vals), n: vals.length } : null;
    });
  }
  return { days, rows };
}

/** Mean delta per hour of day, pooled across days — the heat-retention signature. */
export function hourProfile(points: DeltaPoint[]): Array<CellStat | null> {
  const buckets: number[][] = Array.from({ length: 24 }, () => []);
  for (const p of points) buckets[berlinDayHour(p.ts).hour]!.push(p.delta);
  return buckets.map((v) => (v.length ? { mean: mean(v), n: v.length } : null));
}

export interface DeltaSummary {
  n: number;
  mean: number;
  min: number;
  max: number;
  /** Later-half mean minus earlier-half mean: a drift *hint*, not a diagnosis. */
  shift: number | null;
  /** Spread between the warmest and coolest hour-of-day means. */
  diurnalAmplitude: number | null;
  warmestHour: number | null;
  coolestHour: number | null;
}

export function summarise(points: DeltaPoint[]): DeltaSummary | null {
  if (points.length === 0) return null;
  const deltas = points.map((p) => p.delta);

  let shift: number | null = null;
  if (points.length >= 8) {
    const mid = Math.floor(points.length / 2);
    shift = mean(deltas.slice(mid)) - mean(deltas.slice(0, mid));
  }

  const profile = hourProfile(points);
  const filled = profile
    .map((c, h) => (c ? { h, mean: c.mean } : null))
    .filter((x): x is { h: number; mean: number } => x !== null);

  let diurnalAmplitude: number | null = null;
  let warmestHour: number | null = null;
  let coolestHour: number | null = null;
  if (filled.length >= 6) {
    const hi = filled.reduce((a, b) => (b.mean > a.mean ? b : a));
    const lo = filled.reduce((a, b) => (b.mean < a.mean ? b : a));
    diurnalAmplitude = hi.mean - lo.mean;
    warmestHour = hi.h;
    coolestHour = lo.h;
  }

  return {
    n: points.length,
    mean: mean(deltas),
    min: Math.min(...deltas),
    max: Math.max(...deltas),
    shift,
    diurnalAmplitude,
    warmestHour,
    coolestHour
  };
}

/** Diverging scale: warm where the deck runs hotter, cool where it runs colder. */
export function deltaColor(value: number, scale: number): string {
  if (!Number.isFinite(value) || scale <= 0) return "#f1f5f9";
  const t = Math.min(Math.abs(value) / scale, 1);
  const lightness = 94 - t * 46;
  return value >= 0 ? `hsl(18, 82%, ${lightness.toFixed(1)}%)` : `hsl(207, 72%, ${lightness.toFixed(1)}%)`;
}

function signed(v: number, digits = 1): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}`;
}

function hh(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

export function shortDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  // timeZone must be pinned: the date is UTC midnight, so formatting it in a
  // viewer's negative-offset zone would label it as the day before.
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" })
    .format(new Date(Date.UTC(y!, m! - 1, d!)));
}

function currentDelta(snap: Snapshot): { deck: number; official: number; delta: number } | null {
  const deck = snap.sensor?.measurements.temperature;
  const official = snap.weather?.current?.temperature;
  if (deck == null || official == null) return null;
  return { deck, official, delta: deck - official };
}

export function buildDeltaCard(snap: Snapshot, history: HistoryPoint[], now: Date = new Date()): HTMLElement {
  const card = el("section", { className: "delta-card" });
  card.append(el("h3", { text: "Deck vs DWD — the gap" }));

  const live = currentDelta(snap);
  const headline = el("div", { className: "delta-now" });
  if (live) {
    const word = Math.abs(live.delta) < 0.05 ? "level with" : live.delta > 0 ? "warmer than" : "cooler than";
    const value = el("span", { className: "delta-value", text: `${signed(live.delta)}°C` });
    value.setAttribute("style", `color: ${live.delta >= 0 ? "#c2410c" : "#1d4ed8"};`);
    headline.append(
      value,
      el("span", {
        className: "delta-caption",
        text: `Deck is ${word} DWD right now — ${live.deck.toFixed(1)}°C on the roof, ${live.official.toFixed(1)}°C forecast`
      })
    );
  } else {
    headline.append(el("span", { className: "delta-caption", text: "No live pair — the rooftop box or the forecast is missing." }));
  }
  card.append(headline);

  const points = extractDeltas(history);
  const stats = summarise(points);
  if (!stats) {
    card.append(el("p", {
      className: "delta-note",
      text: "Not enough paired readings in the last 7 days to map the gap yet. " +
            "Readings re-served while the box was silent are excluded, so a long outage leaves this empty."
    }));
    return card;
  }

  card.append(el("p", {
    className: "delta-stats",
    text: `Last 7 days: mean ${signed(stats.mean)}°C · range ${signed(stats.min)} to ${signed(stats.max)}°C · ${stats.n} paired readings`
  }));

  // Day × hour map. Reading across a row shows the daily shape; reading down a
  // column shows whether that hour's gap is changing from day to day.
  const scale = Math.max(Math.abs(stats.min), Math.abs(stats.max), 0.5);
  const grid = buildGrid(points);
  const map = el("div", { className: "delta-grid" });

  const headRow = el("div", { className: "delta-row delta-head" }, [el("span", { className: "delta-daylabel" })]);
  for (let h = 0; h < 24; h++) {
    headRow.append(el("span", { className: "delta-hourlabel", text: h % 6 === 0 ? String(h) : "" }));
  }
  map.append(headRow);

  for (const day of grid.days) {
    const row = el("div", { className: "delta-row" }, [
      el("span", { className: "delta-daylabel", text: shortDay(day), title: day })
    ]);
    for (let h = 0; h < 24; h++) {
      const cell = grid.rows[day]![h];
      const box = el("span", {
        className: cell ? "delta-cell" : "delta-cell is-empty",
        title: cell ? `${day} ${hh(h)} — ${signed(cell.mean)}°C (${cell.n} reading${cell.n === 1 ? "" : "s"})` : `${day} ${hh(h)} — no data`
      });
      if (cell) box.setAttribute("style", `background-color: ${deltaColor(cell.mean, scale)};`);
      row.append(box);
    }
    map.append(row);
  }
  card.append(el("div", { className: "delta-grid-wrap" }, [map]));

  const legend = el("div", { className: "delta-legend" });
  legend.append(el("span", { className: "delta-legend-label", text: `${signed(-scale)}°C` }));
  for (const stop of [-1, -0.6, -0.25, 0, 0.25, 0.6, 1]) {
    const sw = el("span", { className: "delta-swatch" });
    sw.setAttribute("style", `background-color: ${deltaColor(stop * scale, scale)};`);
    legend.append(sw);
  }
  legend.append(el("span", { className: "delta-legend-label", text: `${signed(scale)}°C` }));
  legend.append(el("span", { className: "delta-legend-caption", text: "orange = deck warmer · blue = deck cooler" }));
  card.append(legend);

  const notes = el("div", { className: "delta-notes" });
  if (stats.diurnalAmplitude != null && stats.warmestHour != null && stats.coolestHour != null) {
    notes.append(el("p", {
      text: `The gap swings ${stats.diurnalAmplitude.toFixed(1)}°C across the day — widest around ` +
            `${hh(stats.warmestHour)} and narrowest around ${hh(stats.coolestHour)}. A gap that tracks the ` +
            `clock like this is siting: sun on the roof and heat coming back out of the building, not the sensor.`
    }));
  }
  if (stats.shift != null) {
    notes.append(el("p", {
      text: `Second half of the week runs ${signed(stats.shift)}°C against the first. Real calibration drift ` +
            `would show up as a steady move in one direction at every hour — seven days of changing weather ` +
            `cannot separate that from an ordinary warm or cold spell, so read this as a flag to watch, not a verdict.`
    }));
  }
  notes.append(el("p", {
    text: "The reference is Open-Meteo's forecast value for the Munich grid square (DWD's ICON model), not a " +
          "thermometer next door — so part of every gap is model error and part is the roof being a different " +
          "place from the grid average."
  }));
  card.append(notes);

  return card;
}
